import React, { useState, useEffect } from 'react';
import { Search, IndianRupee, Calendar, Lock, Download } from 'lucide-react';
import SalaryHoldModal from './SalaryHoldModal';
import SalarySlipModal from './SalarySlipModal';
import ProcessPaymentModal from './ProcessPaymentModal';
import toast from 'react-hot-toast';
import api from '../api';

export default function Payroll() {
  const [payroll, setPayroll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedHoldEmployee, setSelectedHoldEmployee] = useState(null);
  const [selectedPayrollForSlip, setSelectedPayrollForSlip] = useState(null);
  const [selectedPaymentRecord, setSelectedPaymentRecord] = useState(null);
  const [isProcessPaymentOpen, setIsProcessPaymentOpen] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchPayroll();
  }, [selectedMonth]);

  const fetchPayroll = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/payroll?month=${selectedMonth}`);
      const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      const transformed = data.map(record => {
        const userName = record.user?.name || 'N/A';
        const nameParts = userName.split(' ').filter(Boolean);
        const initials = nameParts.length > 1 
            ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() 
            : userName.substring(0, 2).toUpperCase();

        return {
          ...record,
          id: record.id || record.employee_id,
          name: userName,
          initials: initials,
          empId: record.human_readable_id || `EMP-${record.user_id || '??'}`,
          designation: record.designation || 'N/A',
        baseSalary: record.basic_salary ? `₹${record.basic_salary}` : '₹0',
        todayEarned: record.today_earning ? `₹${record.today_earning}` : '₹0',
        monthEarned: record.earned_salary ? `₹${record.earned_salary}` : '₹0',
        earned_salary_raw: record.earned_salary || 0, // Store raw for update
        status: record.status || 'Pending',
        avatar: record.user?.profile_photo_url || null
        };
      });
      
      setPayroll(transformed);
    } catch (err) {
      console.error("Error fetching payroll:", err);
      setError('Failed to load payroll data.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    if (newStatus === 'Paid') {
        setSelectedPaymentRecord(record);
        setIsProcessPaymentOpen(true);
        return;
    }

    try {
      await api.post('/payroll', {
        employee_id: record.employee_id,
        month: record.month,
        basic_salary: record.basic_salary,
        allowances: record.allowances || 0,
        deductions: record.deductions || 0,
        net_salary: record.net_salary || record.earned_salary_raw || 0,
        status: newStatus
      });
      fetchPayroll();
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error('Failed to update status. Please try again.');
    }
  };

  const onConfirmPayment = async (paymentData) => {
    try {
      await api.post('/payroll', {
        ...paymentData,
        employee_id: selectedPaymentRecord.employee_id,
        month: selectedPaymentRecord.month,
        basic_salary: selectedPaymentRecord.earned_salary_raw, // Send raw numeric value
        allowances: selectedPaymentRecord.allowances || 0,
        deductions: selectedPaymentRecord.deductions || 0,
      });
      fetchPayroll();
      toast.success('Payment processed successfully!');
      setIsProcessPaymentOpen(false);
    } catch (err) {
      console.error("Error processing payment:", err);
      toast.error('Failed to process payment. Please try again.');
      throw err;
    }
  };

  // Filter & Pagination Logic
  const filtered = payroll.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.empId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.designation.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getPageButtons = () => {
    let pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) pages = [1, 2, 3, '...', totalPages];
      else if (currentPage >= totalPages - 2) pages = [1, '...', totalPages - 2, totalPages - 1, totalPages];
      else pages = [1, '...', currentPage, '...', totalPages];
    }
    return pages;
  };

  if (loading) return <div className="p-20 text-center text-slate-500 font-bold flex flex-col items-center gap-4"><IndianRupee className="animate-bounce" size={40} /> Loading payroll data...</div>;
  if (error) return <div className="p-20 text-center text-red-500 font-bold">{error}</div>;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Top Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3 lg:px-5 lg:py-3.5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-100/60 flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100">
            <IndianRupee size={24} strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Payroll Management</h1>
        </div>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Search Box */}
          <div className="relative flex-1 sm:w-64 border border-gray-200 rounded-md bg-gray-50">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search employee or ID..." 
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to page 1 on search
              }}
              className="w-full pl-9 pr-4 py-2 bg-transparent text-sm focus:outline-none text-slate-700 font-medium placeholder:font-normal"
            />
          </div>
          
          {/* Date Filter */}
          <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-white border border-gray-200 rounded-md shadow-sm transition-colors hover:border-gray-300">
            <FilterIcon size={15} className="text-slate-500" strokeWidth={2.5} />
            <span className="text-sm font-bold text-slate-600">Month:</span>
            <div className="relative flex items-center">
              <input 
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="bg-transparent text-sm font-black text-[#1e293b] outline-none cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-gray-100 text-[11px] uppercase font-black tracking-widest whitespace-nowrap">
                <th className="px-6 py-4 text-slate-500">EMPLOYEE</th>
                <th className="px-4 py-4 text-slate-500">DESGINATION</th>
                <th className="px-4 py-4 text-slate-500">MONTHLY BASE</th>
                <th className="px-4 py-4 text-emerald-600 bg-emerald-50/50">TODAY EARNED</th>
                <th className="px-4 py-4 text-indigo-600 bg-indigo-50/50">MONTH EARNED</th>
                <th className="px-4 py-4 text-slate-500">STATUS</th>
                <th className="px-4 py-4 text-slate-500 text-center">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {currentData.map((record) => (
                <tr key={record.id} className="hover:bg-gray-50/50 transition-colors group cursor-default">
                  
                  {/* EMPLOYEE */}
                  <td className="px-6 py-4 min-w-[200px] align-middle">
                    <div className="flex items-center gap-3">
                      {record.avatar ? (
                        <img src={record.avatar} alt={record.name} className="w-10 h-10 rounded-full object-cover border border-gray-200 shadow-sm" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[13px] font-black tracking-widest uppercase border border-indigo-200 shadow-sm">
                          {record.initials}
                        </div>
                      )}
                      <div>
                        <div className="text-[13px] font-bold text-slate-800 leading-tight">{record.name}</div>
                        <div className="text-[11px] font-semibold text-gray-400 mt-0.5">{record.empId}</div>
                      </div>
                    </div>
                  </td>

                  {/* DESGINATION */}
                  <td className="px-4 py-4 align-middle">
                    <span className="text-xs font-bold text-slate-600">
                      {record.designation}
                    </span>
                  </td>

                  {/* BASE SALARY */}
                  <td className="px-4 py-4 align-middle">
                    <span className="text-xs font-bold text-slate-600">
                      {record.baseSalary}
                    </span>
                  </td>

                  {/* TODAY EARNED */}
                  <td className="px-4 py-4 bg-emerald-50/30 align-middle">
                    <span className="text-sm font-black text-emerald-600">
                      {record.todayEarned}
                    </span>
                  </td>

                  {/* MONTH EARNED */}
                  <td className="px-4 py-4 bg-indigo-50/30 align-middle">
                    <span className="text-sm font-black text-indigo-600" title="Actual Days Worked Salary">
                      {record.monthEarned}
                    </span>
                  </td>

                  {/* STATUS */}
                  <td className="px-4 py-4 align-middle">
                    <select 
                      value={record.status}
                      onChange={(e) => handleStatusChange(record, e.target.value)}
                      className={`appearance-none pl-3 pr-7 h-8 rounded-full border text-[11px] font-black tracking-wide shadow-sm focus:outline-none cursor-pointer border-transparent transition-all leading-none ${
                        record.status === 'Paid' 
                          ? 'bg-emerald-100/80 text-emerald-700' 
                          : record.status === 'Delayed'
                          ? 'bg-red-100/80 text-red-700'
                          : 'bg-amber-100/80 text-amber-700'
                      }`}
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${record.status === 'Paid' ? '%23047857' : record.status === 'Delayed' ? '%23b91c1c' : '%23b45309'}' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.4rem center',
                        backgroundSize: '12px'
                      }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                  </td>

                  {/* ACTIONS */}
                  <td className="px-4 py-4 text-center align-middle">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => setSelectedHoldEmployee(record)}
                        className="w-8 h-8 rounded-full border border-red-100 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors shadow-sm"
                        title="Put Salary On Hold"
                      >
                        <Lock size={14} strokeWidth={2.5} />
                      </button>
                      <button 
                        onClick={() => setSelectedPayrollForSlip(record)}
                        className="w-8 h-8 rounded-full border border-blue-100 bg-blue-50 text-blue-500 hover:bg-blue-500 hover:text-white flex items-center justify-center transition-colors shadow-sm"
                        title="Download Salary Slip"
                      >
                        <Download size={14} strokeWidth={2.5} />
                      </button>
                    </div>
                  </td>

                </tr>
              ))}
              {currentData.length === 0 && (
                <tr>
                   <td colSpan="7" className="px-6 py-20 text-center text-slate-400 font-bold">
                      No employees found matching your search.
                   </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-6 py-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs font-bold text-slate-500">
            Showing <span className="text-slate-800">{filtered.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to <span className="text-slate-800">{Math.min(currentPage * itemsPerPage, filtered.length)}</span> of <span className="text-slate-800">{filtered.length}</span> entries
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors border border-transparent shadow-sm hover:border-gray-200"
            >
              Previous
            </button>
            {getPageButtons().map((p, idx) => (
              p === '...' ? (
                <span key={`dots-${idx}`} className="text-slate-400 font-black px-1">...</span>
              ) : (
                <button 
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-md shadow-sm transition-all ${currentPage === p ? 'bg-[#10b981] text-white' : 'text-slate-600 hover:bg-gray-50 border border-transparent hover:border-gray-200'}`}
                >
                  {p}
                </button>
              )
            ))}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors border border-transparent shadow-sm hover:border-gray-200"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <SalaryHoldModal 
        isOpen={!!selectedHoldEmployee} 
        onClose={() => setSelectedHoldEmployee(null)} 
        employee={selectedHoldEmployee} 
      />

      <SalarySlipModal 
        isOpen={!!selectedPayrollForSlip}
        onClose={() => setSelectedPayrollForSlip(null)}
        payroll={selectedPayrollForSlip}
      />

      <ProcessPaymentModal
        isOpen={isProcessPaymentOpen}
        onClose={() => setIsProcessPaymentOpen(false)}
        record={selectedPaymentRecord}
        onConfirm={onConfirmPayment}
      />
    </div>
  );
}

function FilterIcon({ size, className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
